import React, { useMemo, useState } from 'react';
import {
  FiCalendar,
  FiEdit2,
  FiEye,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';
import useUpcomingExams from '../../hooks/useUpcomingExams';
import {
  formatDDay,
  formatKoreanDate,
  getDaysUntil,
} from '../../utils/dateUtils';
import ExamDetailModal from './ExamDetailModal';
import ExamFormModal from './ExamFormModal';

const UpcomingExamSection = () => {
  const {
    error,
    exams,
    isAuthenticated,
    isLoading,
    loadExams,
    removeExam,
    saveExam,
  } = useUpcomingExams();
  const [editingExam, setEditingExam] = useState(null);
  const [detailExam, setDetailExam] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [actionError, setActionError] = useState('');

  const upcomingExams = useMemo(() => (
    exams
      .map((exam) => ({
        ...exam,
        examStartDate: exam.examStartDate || exam.examDate,
        schedules: (Array.isArray(exam.schedules) ? exam.schedules : [])
          .filter((schedule) => getDaysUntil(schedule.examDate) >= 0)
          .sort((first, second) => (
            first.examDate.localeCompare(second.examDate)
            || String(first.examPeriod || '').localeCompare(String(second.examPeriod || ''), 'ko-KR', { numeric: true })
            || first.subjectName.localeCompare(second.subjectName, 'ko-KR')
          )),
      }))
      .filter((exam) => getDaysUntil(exam.examStartDate) >= 0)
      .map((exam) => ({
        ...exam,
        examDate: exam.examStartDate,
      }))
      .sort((first, second) => first.examDate.localeCompare(second.examDate))
  ), [exams]);

  const openForm = (exam = null) => {
    setEditingExam(exam);
    setActionError('');
    setIsFormOpen(true);
  };

  const handleDelete = async (exam) => {
    if (!window.confirm(`${exam.examTitle} 일정을 삭제하시겠습니까?`)) return;
    setActionError('');
    try {
      await removeExam(exam.sno);
    } catch (deleteError) {
      setActionError(deleteError.message || '시험 일정을 삭제하지 못했습니다.');
    }
  };

  return (
    <>
      <section className="scheduler-exams" aria-label="시험 관리">
        <div className="scheduler-exams__header">
          <button type="button" onClick={() => openForm()} disabled={!isAuthenticated}>
            <FiPlus aria-hidden="true" /> 시험 일정 추가
          </button>
        </div>

        {!isAuthenticated && (
          <div className="scheduler-exams__empty is-auth">
            <FiCalendar aria-hidden="true" />
            <span>학생 로그인 후 시험 일정을 관리할 수 있습니다.</span>
          </div>
        )}

        {isAuthenticated && (error || actionError) && (
          <div className="scheduler-exams__error" role="alert">
            <span>{error || actionError}</span>
            {error && <button type="button" onClick={loadExams}>다시 시도</button>}
          </div>
        )}

        {isAuthenticated && isLoading && (
          <div className="scheduler-exams__loading" aria-label="시험 일정 불러오는 중">
            <i /><i /><i />
          </div>
        )}

        {isAuthenticated && !isLoading && !error && upcomingExams.length === 0 && (
          <div className="scheduler-exams__empty">
            <span><FiCalendar aria-hidden="true" /></span>
            <div>
              <strong>예정된 시험이 없습니다.</strong>
              <small>날짜별 시험 과목을 등록하면 남은 날짜를 자동으로 계산합니다.</small>
            </div>
            <button type="button" onClick={() => openForm()}>첫 시험 등록</button>
          </div>
        )}

        {isAuthenticated && !isLoading && upcomingExams.length > 0 && (
          <div className="scheduler-exams__list">
            {upcomingExams.map((exam) => {
              const daysUntil = getDaysUntil(exam.examDate);
              const urgencyClass = daysUntil <= 7 ? 'is-imminent' : daysUntil <= 30 ? 'is-soon' : '';
              return (
                <article className={`scheduler-exam-card ${urgencyClass}`.trim()} key={exam.sno}>
                  <div className="scheduler-exam-card__content">
                    <div className="scheduler-exam-card__title">
                      <span className="scheduler-exam-card__countdown">{formatDDay(exam.examDate)}</span>
                      <strong>{exam.examTitle}</strong>
                    </div>
                    <span className="scheduler-exam-card__start-date">
                      <FiCalendar aria-hidden="true" /> 시험 시작 {formatKoreanDate(exam.examStartDate)}
                    </span>
                  </div>
                  <div className="scheduler-exam-card__actions">
                    <button className="is-detail" type="button" onClick={() => setDetailExam(exam)} aria-label={`${exam.examTitle} 상세`}>
                      <FiEye aria-hidden="true" /><span>상세</span>
                    </button>
                    <button type="button" onClick={() => openForm(exam)} aria-label={`${exam.examTitle} 수정`}>
                      <FiEdit2 aria-hidden="true" />
                    </button>
                    <button type="button" onClick={() => handleDelete(exam)} aria-label={`${exam.examTitle} 삭제`}>
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {isFormOpen && (
        <ExamFormModal
          initialExam={editingExam}
          onClose={() => setIsFormOpen(false)}
          onSave={saveExam}
        />
      )}

      {detailExam && (
        <ExamDetailModal
          exam={detailExam}
          onClose={() => setDetailExam(null)}
          onEdit={() => {
            const exam = detailExam;
            setDetailExam(null);
            openForm(exam);
          }}
        />
      )}
    </>
  );
};

export default UpcomingExamSection;
